import { forwardRef, Ref } from 'react';
import { Skeleton } from '@mofid-securities/uikit';
import { FieldValues, FormState } from 'react-hook-form';
import { StickyFooter } from '@/layouts/StickyFooter';
import Button from '@/components/Button';
import { FormConsumer } from '@/components/HookForm/index';
import { useRouteChangeListener } from '@/hooks/routeChangeListener';
import { useHandleSubmitBasedOnUserStatus } from './FormSubmitButton.hooks';
import type { IFormSubmitButtonProps } from './FormSubmitButton.types';

const FormSubmitButton = (
  {
    children,
    disabled,
    isLoading,
    disabledIfNotValid = true,
    isSticky = true,
    type = 'submit',
    shouldShowSkeleton = false,
    forbiddenUserStatus,
    onClick,
    ...props
  }: IFormSubmitButtonProps,
  ref: Ref<HTMLButtonElement>
) => {
  const { handleClick, selectedType } = useHandleSubmitBasedOnUserStatus({
    forbiddenUserStatus,
    onClick,
    type,
  });
  const { isRouteChangeStarted } = useRouteChangeListener();

  if (shouldShowSkeleton) {
    return <Skeleton height={48} />;
  }

  const isButtonDisabled = (formState: FormState<FieldValues>) =>
    disabled ||
    formState.isSubmitting ||
    (disabledIfNotValid && !formState.isValid) ||
    isRouteChangeStarted;

  const isButtonLoading = (formState: FormState<FieldValues>) =>
    isLoading || formState?.isSubmitting || isRouteChangeStarted;

  const content = (
    <FormConsumer
      render={({ formState }) => (
        <Button
          ref={ref}
          disabled={isButtonDisabled(formState)}
          isLoading={isButtonLoading(formState)}
          type={selectedType}
          isFullWidth
          onClick={handleClick}
          {...props}
        >
          {children}
        </Button>
      )}
    />
  );

  if (isSticky) {
    return <StickyFooter>{content}</StickyFooter>;
  } else return content;
};

export default forwardRef<HTMLButtonElement, IFormSubmitButtonProps>(
  FormSubmitButton
);
